import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { apiClient } from '@/api/client'
import { useNotificationContext } from '@/features/notifications'
import { useGeneralSettings } from '@/features/general-settings/hooks/useGeneralSettings'
import { App as CapacitorApp } from '@capacitor/app'
import { setUpdateBlocker } from '@/lib/appUpdate'
import { AppUpdater } from '@/plugins/app-updater'
import { usePrintOrder } from '../hooks/usePrintOrder'
import type { DeepOrder } from '../types'

const AUTO_PRINT_STORAGE_KEY = 'printer_auto_print'
const PENDING_JOBS_STORAGE_PREFIX = 'printer_pending_jobs'
const PENDING_JOB_MAX_AGE_MS = 2 * 60 * 60 * 1000
export const ORDER_CONFIRMED_EVENT = 'orders:confirmed'

/** Per-device switch, on by default like the old RestaurantApp. */
export function isAutoPrintEnabled(): boolean {
  try {
    return localStorage.getItem(AUTO_PRINT_STORAGE_KEY) !== 'false'
  } catch {
    return true
  }
}

export function setAutoPrintEnabled(enabled: boolean) {
  try {
    localStorage.setItem(AUTO_PRINT_STORAGE_KEY, String(enabled))
  } catch {
    // Without storage the default (on) applies.
  }
}

interface PrintJob {
  orderId: number
  /** 'new' prints on arrival; with order confirmation enabled the ticket is printed on confirm instead (old app behaviour). */
  reason: 'new' | 'confirmed'
  queuedAt: number
}

// The order stream remembers alerted orders across restarts, so a job that was queued but not
// printed before a reload, crash or app update would otherwise never print. Keep the queue too.
export function loadPendingJobs(restaurantId: number): PrintJob[] {
  try {
    const stored = JSON.parse(localStorage.getItem(`${PENDING_JOBS_STORAGE_PREFIX}:${restaurantId}`) ?? '[]') as unknown
    if (!Array.isArray(stored)) return []
    const cutoff = Date.now() - PENDING_JOB_MAX_AGE_MS
    return stored.filter((job: unknown): job is PrintJob => {
      if (typeof job !== 'object' || job === null) return false
      const { orderId, reason, queuedAt } = job as Record<string, unknown>
      return (
        Number.isSafeInteger(orderId) &&
        (reason === 'new' || reason === 'confirmed') &&
        typeof queuedAt === 'number' &&
        queuedAt > cutoff
      )
    })
  } catch {
    return []
  }
}

export function savePendingJobs(restaurantId: number, jobs: PrintJob[]) {
  // A reload for an app update must not interrupt queued or running tickets.
  setUpdateBlocker('auto-print', jobs.length > 0)
  try {
    localStorage.setItem(`${PENDING_JOBS_STORAGE_PREFIX}:${restaurantId}`, JSON.stringify(jobs))
  } catch {
    // Without storage the queue only lives as long as this page.
  }
}

/** Prints orders without a tap, like the old RestaurantApp. Mounted on Android only, once per selected restaurant. */
export function AutoPrinter({ restaurantId }: { restaurantId: number }) {
  const { t } = useTranslation('orders')
  const { subscribeNewOrders } = useNotificationContext()
  const { settings } = useGeneralSettings(restaurantId)
  const { printOrder, isReady } = usePrintOrder()

  const queueRef = useRef<PrintJob[]>(loadPendingJobs(restaurantId))
  const runningRef = useRef(false)
  const latestRef = useRef({ printOrder, isReady, settings })
  latestRef.current = { printOrder, isReady, settings }
  const enqueueRef = useRef<(job: PrintJob) => void>(() => undefined)
  // While the old RestaurantApp is still installed it prints too and holds the printer: no double tickets.
  const oldAppInstalledRef = useRef(false)

  useEffect(() => {
    const check = () => {
      AppUpdater.findLegacyApps()
        .then((result) => { oldAppInstalledRef.current = result.packages.length > 0 })
        .catch(() => undefined)
    }
    check()
    const listener = CapacitorApp.addListener('resume', check)
    return () => {
      void listener.then((handle) => handle.remove())
    }
  }, [])

  // One job at a time: a Bluetooth printer accepts a single connection.
  const drain = useCallback(async () => {
    if (runningRef.current) return
    runningRef.current = true
    try {
      while (queueRef.current.length > 0 && latestRef.current.isReady && latestRef.current.settings) {
        const job = queueRef.current[0]
        const confirmationEnabled = latestRef.current.settings.orderConfirmationEnabled
        if (job.reason === 'confirmed' || !confirmationEnabled) {
          try {
            const { data: order } = await apiClient.get<DeepOrder>(
              `/api/admin/restaurants/${restaurantId}/orders/${job.orderId}`
            )
            await latestRef.current.printOrder(order, undefined, true)
          } catch (error) {
            console.error(`Auto-print of order ${job.orderId} failed`, error)
            toast.error(t('printer.autoPrintFailed', { orderId: job.orderId }), {
              duration: Infinity,
              action: { label: t('printer.retry'), onClick: () => enqueueRef.current(job) },
            })
          }
        }
        queueRef.current.shift()
        savePendingJobs(restaurantId, queueRef.current)
      }
    } finally {
      runningRef.current = false
    }
  }, [restaurantId, t])

  enqueueRef.current = (job: PrintJob) => {
    if (!queueRef.current.some((queued) => queued.orderId === job.orderId && queued.reason === job.reason)) {
      queueRef.current.push(job)
      savePendingJobs(restaurantId, queueRef.current)
    }
    void drain()
  }

  // Jobs that arrived while menu, label or general settings were still loading.
  useEffect(() => {
    if (isReady && settings) void drain()
  }, [isReady, settings, drain])

  useEffect(
    () =>
      subscribeNewOrders((event) => {
        if (isAutoPrintEnabled() && !oldAppInstalledRef.current) enqueueRef.current({ orderId: event.orderId, reason: 'new', queuedAt: Date.now() })
      }),
    [subscribeNewOrders]
  )

  useEffect(() => {
    const handleConfirmed = (event: Event) => {
      const orderId = (event as CustomEvent<number>).detail
      if (isAutoPrintEnabled() && !oldAppInstalledRef.current) enqueueRef.current({ orderId, reason: 'confirmed', queuedAt: Date.now() })
    }
    window.addEventListener(ORDER_CONFIRMED_EVENT, handleConfirmed)
    return () => window.removeEventListener(ORDER_CONFIRMED_EVENT, handleConfirmed)
  }, [])

  return null
}
